import {useEffect, useRef, useState} from 'react';
import {WdiSelect, WdiSelectFormat, WdiTreeSelect} from '@/components/WdiControls/WdiSelect';
import {Checkbox, Form, message} from 'antd';
import {ResponseStatus} from '@/utils/request';
import {WdiInputNumber} from '@/components/WdiControls/WdiInputNumber';
import {Product, Uom} from '@/services/mdm/product';
import {ProFormInstance} from '@ant-design/pro-form';
import {FieldProps, wdiCore, WdiModalAppProps} from '@/components/WdiControls';
import {HorzLayoutOpts, WdiModalForm} from '@/components/WdiControls/WdiModalForm';
import {FormSa} from '@/services/sales/sa';
import {ProductPrice} from '@/services/mdm/productprice';
import {buildFormItem, getPdDetailPrevField} from "@/pages/sales/sa/PriceDiscountApply/components/DetailBase";
import {responseIsOk} from "@/utils/response";
import moment from "moment/moment";
import {
  PRODUCT_BY_QTY_LEVELS,
  PRODUCT_BY_QTY_DISABLED_LEVELS
} from "@/pages/sales/sa/GeneralActivityApply/components/DetailBase";

const PdBuyGiveDetailApp: React.FC<WdiModalAppProps> = (props) => {
  const formRef = useRef<ProFormInstance>();
  const {formSaDetail, formSa, rowIndex, rows} = props.data;
  const [expandKeysProduct, setExpandKeysProduct] = useState<{ buy: string[], free: string[] }>();

  useEffect(() => {
    if (formSaDetail) {
      formRef.current?.setFieldsValue({
        ecName: formSaDetail.ecName,
        revenueTypeName: formSaDetail.revenueTypeName,
        productBuyIds: formSaDetail.productBuyIds,
        productFreeIds: formSaDetail.productFreeIds,
        quantityBuy: formSaDetail.quantityBuy,
        quantityFree: formSaDetail.quantityFree,
        quantityBomForecast: formSaDetail.quantityBomForecast,
        revenueSellinForecast: formSaDetail.revenueSellinForecast,
        priceSellinBefore: formSaDetail.priceSellinBefore,
        priceSellinFreeProduct: formSaDetail.priceSellinFreeProduct,
        quantityMin: formSaDetail.quantityMin,
        revenueMin: formSaDetail.revenueMin,
        isLimited: formSaDetail.isLimited,
        amount: formSaDetail.amount,
        remark: formSaDetail.remark,
      });
      if (formSaDetail.activityStartDate) {
        formRef.current?.setFieldsValue({
          activityDate: [
            moment(formSaDetail.activityStartDate),
            moment(formSaDetail.activityEndDate),
          ]
        });
      }

      if (formSaDetail.supplyStartDate) {
        formRef.current?.setFieldsValue({
          supplyDate: [
            moment(formSaDetail.supplyStartDate),
            moment(formSaDetail.supplyEndDate),
          ]
        });
      }

      if (formSaDetail.productBuyIds) {
        Product.searchAll({productIds: formSaDetail.productBuyIds, isActive: true}).then(response => {
          if (response?.code === ResponseStatus.SUCCESS && response.data) {
            formRef.current?.setFieldsValue({
              productBuyIds: response.data.map(m => ({
                value: m.productId,
                label: m.codeOrName
              }))
            });
          }
        });
        handleProductBuyChange(formSaDetail.productBuyIds).then();
        setExpandKeysProduct(Object.assign({}, expandKeysProduct, {buy: formSaDetail.productBuyIds.split(",")}))
      }
      if (formSaDetail.productFreeIds) {
        Product.searchAll({productIds: formSaDetail.productFreeIds, isActive: true}).then(response => {
          if (response?.code === ResponseStatus.SUCCESS && response.data) {
            formRef.current?.setFieldsValue({
              productFreeIds: response.data.map(m => ({
                value: m.productId,
                label: m.codeOrName
              }))
            });
          }
        });
        handleProductFreeChange(formSaDetail.productFreeIds).then();
        setExpandKeysProduct(Object.assign({}, expandKeysProduct, {free: formSaDetail.productFreeIds.split(",")}))
      }

      Uom.getZeroUom().then(response => {
        formRef.current?.setFieldsValue({
          uomCode: response.data.uomCode
        });
      });
    }
  }, [formSaDetail]);

  const handleProductBuyChange = async (value: any) => {
    formRef.current?.setFieldsValue({priceSellinBefore: null});
    if (value) {
      const priceResp = await ProductPrice.getRegionPriceSellin2({
        productIds: WdiSelectFormat.getvalue(value),
        boIds: formSa.boIds,
        custSysCode: formSa.custSysCode,
        custTypeId: formSa.custTypeId,
      });
      if (!responseIsOk(priceResp)) {
        message.error(priceResp.message);
        return;
      }
      formRef.current?.setFieldsValue({priceSellinBefore: priceResp.data.priceInVat});
    }
    calculateAmount();
  };

  const handleProductFreeChange = async (value: any) => {
    formRef.current?.setFieldsValue({priceSellinFreeProduct: null});
    if (value) {
      const priceResp = await ProductPrice.getProductPriceFg({
        productIds: WdiSelectFormat.getvalue(value),
      });
      if (!responseIsOk(priceResp)) {
        message.error(priceResp.message);
        return;
      }
      if (priceResp.data.priceInVat) {
        formRef.current?.setFieldsValue({priceSellinFreeProduct: priceResp.data.priceInVat});
      }
    }
    calculateAmount();
  };

  // 预计出货销售额	计算列：= 购买产品原供货价 * 每套购买数量 * 预计购买套数; 2位小数；不为空；revenue_sellin_forecast
  // 申请金额 计算列：计算列：赠送产品的免费货固定价（多个取最大值） * 每套赠送数量 * 预计购买套数
  // 预计费比 计算列：申请金额/预计出货销售额
  const calculateAmount = () => {
    const fieldsValues = {
      revenueSellinForecast: null, amount: null, roiForecast: null
    };
    const {
      priceSellinBefore, priceSellinFreeProduct, quantityBuy, quantityFree, quantityBomForecast
    } = formRef.current?.getFieldsValue();

    if (!wdiCore.Object.IsNullOrUndefined(priceSellinBefore)
      && !wdiCore.Object.IsNullOrUndefined(quantityBuy)
      && !wdiCore.Object.IsNullOrUndefined(quantityBomForecast)) {
      fieldsValues.revenueSellinForecast = wdiCore.Number.ToNumber(priceSellinBefore * quantityBuy * quantityBomForecast, 2);
    }
    if (!wdiCore.Object.IsNullOrUndefined(priceSellinFreeProduct)
      && !wdiCore.Object.IsNullOrUndefined(quantityFree)
      && !wdiCore.Object.IsNullOrUndefined(quantityBomForecast)) {
      fieldsValues.amount = wdiCore.Number.ToNumber(priceSellinFreeProduct * quantityFree * quantityBomForecast, 2);
    }
    if (fieldsValues.amount && fieldsValues.revenueSellinForecast) {
      fieldsValues.roiForecast = wdiCore.Number.ToNumber(fieldsValues.amount / fieldsValues.revenueSellinForecast, 4);
    }
    formRef.current?.setFieldsValue(fieldsValues);
  }

  return (
    <WdiModalForm
      {...HorzLayoutOpts}
      formRef={formRef}
      title={props.title}
      visible={props.visible}
      modalProps={{
        onCancel: () => {
          props.onClose?.()
        },
      }}
      onFinish={async (values) => {
        const {productBuyIds, productFreeIds, activityDate, supplyDate, ...entity} = values;
        entity.productBuyIds = WdiSelectFormat.getvalue(productBuyIds);
        entity.productFreeIds = WdiSelectFormat.getvalue(productFreeIds);

        if (activityDate && activityDate.length == 2) {
          entity.activityStartDate = moment(activityDate[0]).format('YYYY-MM-DD');
          entity.activityEndDate = moment(activityDate[1]).format('YYYY-MM-DD');
        }
        if (supplyDate && supplyDate.length == 2) {
          entity.supplyStartDate = moment(supplyDate[0]).format('YYYY-MM-DD');
          entity.supplyEndDate = moment(supplyDate[1]).format('YYYY-MM-DD');
        }

        const response = await FormSa.Buttons.validateDetail({
          rowIndex: rowIndex,
          formSa: {...formSa},
          formSaDetail: Object.assign({}, getPdDetailPrevField(formSaDetail), entity),
          formSaDetails: rows
        });
        if (response.code !== ResponseStatus.SUCCESS) {
          message.error(response.message);
          return;
        }
        if (props.onSave) {
          response.data = Object.assign({}, {rowIndex: rowIndex}, response.data);
          props.onSave(response);
        }
        return;
      }}
    >
      {buildFormItem("activityDate")}
      {buildFormItem("supplyDate")}

      <Form.Item name="productBuyIds" label="购买产品" rules={[{required: true, message: '购买产品不能为空!'}]}>
        <WdiTreeSelect {...{
          multiple: true,
          placeholder: "请选择购买产品",
          defaultExpandKeys: expandKeysProduct?.buy,
          dataLoading: async (params) => {
            const response = await Product.searchAll(
              Object.assign({
                productLevels: PRODUCT_BY_QTY_LEVELS.join(),
                isActive: true,
              }, params)
            );
            const {treeData, dict} = FieldProps.TreeSelect.formatTreeData({
              textField: "codeOrName",
              valueField: "productId",
              id: "productId",
              pId: "parentId",
              level: "productLevel",
              disabledLevel: PRODUCT_BY_QTY_DISABLED_LEVELS,
            }, response.data);
            return {treeData, dict};
          },
          onExSelect: (value: any, _: any, dict: any) => {
            const values = formRef.current?.getFieldValue("productBuyIds");
            const rtv = FieldProps.TreeSelect.labeledValuesMeger(dict, values, {value});
            formRef.current?.setFieldsValue({
              productBuyIds: rtv
            });
            handleProductBuyChange(rtv).then();
          },
        }} />
      </Form.Item>
      <Form.Item name="priceSellinBefore" label="priceSellinBefore" hidden>
        <WdiInputNumber/>
      </Form.Item>
      <Form.Item name="productFreeIds" label="赠送产品" rules={[{required: true, message: '赠送产品不能为空!'}]}>
        <WdiTreeSelect {...{
          multiple: true,
          placeholder: "请选择赠送产品",
          defaultExpandKeys: expandKeysProduct?.buy,
          dataLoading: async (params) => {
            const response = await Product.searchAll(
              Object.assign({
                productLevels: PRODUCT_BY_QTY_LEVELS.join(),
                isActive: true,
              }, params)
            );
            const {treeData, dict} = FieldProps.TreeSelect.formatTreeData({
              textField: "codeOrName",
              valueField: "productId",
              id: "productId",
              pId: "parentId",
              level: "productLevel",
              disabledLevel: PRODUCT_BY_QTY_DISABLED_LEVELS,
            }, response.data);
            return {treeData, dict};
          },
          onExSelect: (value: any, _: any, dict: any) => {
            const values = formRef.current?.getFieldValue("productFreeIds");
            const rtv = FieldProps.TreeSelect.labeledValuesMeger(dict, values, {value});
            formRef.current?.setFieldsValue({
              productFreeIds: rtv
            });
            handleProductFreeChange(rtv).then();
          },
        }} />
      </Form.Item>
      <Form.Item name="priceSellinFreeProduct" label="priceSellinFreeProduct" hidden>
        <WdiInputNumber/>
      </Form.Item>
      <Form.Item name="uomCode" label="单位" rules={[{required: true, message: '单位不能为空!'}]}>
        <WdiSelect {...{
          label: "单位",
          disabled: true,
          dataLoading: async (params) => {
            const response = await Uom.search(Object.assign({}, params));
            return response.data.map((item) => ({value: item.uomCode, label: item.uomName}));
          },
        }} />
      </Form.Item>
      <Form.Item
        name="quantityBuy" label="每套购买数量(箱/大袋)"
        rules={[{required: true, message: '每套购买数量不能为空!'}]}>
        <WdiInputNumber min={0} digits={3} onChange={() => {
          calculateAmount();
        }}/>
      </Form.Item>
      <Form.Item
        name="quantityFree" label="每套赠送数量(箱/大袋)"
        rules={[{required: true, message: '每套赠送数量不能为空!'}]}>
        <WdiInputNumber min={0} digits={3} onChange={() => {
          calculateAmount();
        }}/>
      </Form.Item>
      <Form.Item
        name="quantityBomForecast" label="预计购买套数"
        rules={[{required: true, message: '预计购买套数不能为空!'}]}>
        <WdiInputNumber min={0} digits={0} onChange={() => {
          calculateAmount();
        }}/>
      </Form.Item>
      <Form.Item
        name="revenueSellinForecast" label="预计出货销售额"
        rules={[{required: true, message: '预计出货销售额不能为空!'}]}>
        <WdiInputNumber readOnly/>
      </Form.Item>
      <Form.Item name="quantityMin" label="起订量(箱/大袋)">
        <WdiInputNumber min={0} digits={0}/>
      </Form.Item>
      <Form.Item name="revenueMin" label="起订金额">
        <WdiInputNumber min={0}/>
      </Form.Item>
      <Form.Item name="isLimited" label="是否限量" valuePropName="checked">
        <Checkbox/>
      </Form.Item>
      <Form.Item name="amount" label="申请金额" rules={[{required: true, message: '申请金额不能为空!'}]}>
        <WdiInputNumber readOnly/>
      </Form.Item>

      {buildFormItem("remark")}
      {buildFormItem("roiForecast")}
      {buildFormItem("ecName")}
      {buildFormItem("revenueTypeName")}
    </WdiModalForm>
  );
};

export default PdBuyGiveDetailApp;
